import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Unstable_Grid2';
import AddressCard from '@/components/AddressRenderer/AddressListing/AddressCard';
import AddressDictionary from '@/types/dictionaries/Address';
import { Address } from '@/types/gearApi/Address';

const FADE_OUT_PERCENTAGE = 10;

interface AddressSelectionDialogContentProps {
	addresses: Address[];
	dict: AddressDictionary;
	searchTerm: string;
	selectedAddress: Address | undefined;
	onSelect: (address: Address) => void;
	isBillingProfileSelection?: boolean;
}

export default function AddressSelectionDialogContent({
	addresses,
	dict,
	searchTerm,
	selectedAddress,
	onSelect,
	isBillingProfileSelection = false,
}: Readonly<AddressSelectionDialogContentProps>) {
	const isAddressMatchingSearchTerm = (address: Address) => {
		if (!searchTerm) {
			return true;
		}
		const lowerCaseSearchTerm = searchTerm.toLowerCase();
		return [
			address.addressContact.businessUnitName,
			address.addressData.city,
			address.addressData.state,
			address.addressData.country,
		].some((field) => field?.toLowerCase().includes(lowerCaseSearchTerm));
	};

	const filteredBusinessUnitAddresses = addresses.filter(isAddressMatchingSearchTerm);

	// Extract the fading logic into a hook and call it, e.g., 'useScrollFading'
	const dialogContent = useRef<HTMLElement | null>(null);
	const [topFadeOut, setTopFadeOut] = useState(0);
	const [bottomFadeOut, setBottomFadeOut] = useState(0);

	const getScrollTop = () => dialogContent.current?.scrollTop ?? 0;
	const getScrollBottom = () => {
		if (!dialogContent.current) {
			return 0;
		}
		return dialogContent.current.scrollHeight - dialogContent.current.clientHeight - dialogContent.current.scrollTop;
	};

	const setFadeOut = useCallback(() => {
		setTopFadeOut(Math.min(FADE_OUT_PERCENTAGE, getScrollTop()));
		setBottomFadeOut(Math.min(FADE_OUT_PERCENTAGE, getScrollBottom()));
	}, [setTopFadeOut, setBottomFadeOut]);

	useLayoutEffect(() => {
		setFadeOut();
		const resizeObserver = new ResizeObserver(setFadeOut);
		if (dialogContent.current) {
			resizeObserver.observe(dialogContent.current);
		}
		return () => resizeObserver.disconnect();
	}, [setFadeOut]);

	return (
		<DialogContent
			sx={{
				p: '0px',
				mb: '20px',
				maskImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, black ${topFadeOut}%, black ${100 - bottomFadeOut}%, rgba(0, 0, 0, 0) 100%)`,
				height: '480px',
			}}
			onScroll={setFadeOut}
			ref={dialogContent}
		>
			<Grid container rowSpacing={3} columnSpacing={2} mr={0} mb={0}>
				{filteredBusinessUnitAddresses.map((address) => (
					<Grid xs={12} sm={6} md={6} key={address.id}>
						<AddressCard
							dict={dict}
							address={address}
							hasRadioButton
							disableAddressChanges
							isSelected={selectedAddress?.id === address.id}
							onSelect={() => onSelect(address)}
							cardTitle={isBillingProfileSelection ? address.addressContact.businessUnitName : undefined}
						/>
					</Grid>
				))}
			</Grid>
		</DialogContent>
	);
}
